import { useEffect, useMemo, useState } from 'react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { ExternalLink, Receipt as ReceiptIcon } from 'lucide-react';
import {
  Detail,
  DetailList,
  EmptyState,
  Page,
  PageBody,
  PageHeader,
  Panel,
  Skeleton,
  Stat,
  StatRow,
  Toolbar,
} from '@/ui/Panel';
import { DataTable, type Column } from '@/ui/DataTable';
import { SplitView } from '@/ui/SplitView';
import { ChipGroup, Pagination } from '@/ui/Filters';
import { Button } from '@/ui/Button';
import { Select } from '@/ui/Field';
import { SearchBar } from '@/features/transactions/SearchBar';
import type { TxnFilters } from '@/features/transactions/queryParse';
import { Badge, Code } from '@/ui/Badge';
import { ErrorState } from '@/ui/Panel';
import {
  DateRangeBar,
  describeRange,
  isRangePreset,
  resolveRange,
  type RangePreset,
} from '@/ui/DateRange';
import {
  ReceiptLines,
  ReceiptTenders,
  ReceiptTotals,
  TxnStatusBadge,
} from '@/ui/Receipt';
import { useDebouncedValue } from '@/ui/MultiSelect';
import { api, errorMessage } from '@/lib/api/client';
import { isUuid, mapTransactionDetail } from '@/lib/api/mappers';
import { useShortcutHint } from '@/app/StatusLine';
import { dateTime, humanize, money, num, smartDate } from '@/lib/format';
import type { RegisterSummary, TransactionStatus, UserSummary } from '@/lib/api/types';
import { asInt, asOptionalText, asPage, asText } from '@/lib/searchParams';

/**
 * The receipt browser.
 *
 * `GET /transactions` is one of the two endpoints that break the paginated
 * envelope — it answers `{rows, total, limit, offset, summary}`. The `summary`
 * is filter-wide, not page-wide, which is exactly what the KPI band needs: an
 * operator asking "how did today go" wants the whole day, not the first 100
 * receipts of it.
 *
 * The list rows carry `registerId` but no register name, so names are resolved
 * from `/registers`. Selecting a row fills the inspector and writes `?id=`;
 * the full receipt (with the PDF, WhatsApp and reassignment actions) is a
 * deliberate second click.
 */

const PAGE_SIZE = 100;

type StatusFilter = TransactionStatus | 'all';

// `suspended` is deliberately not offered: a parked cart never becomes a
// transactions row (it lives in /recovery until finalized or voided), so the
// enum value only exists for legacy-bridge imports that never carried it.
const STATUS_OPTIONS: Array<{ value: StatusFilter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'completed', label: 'Completed' },
  { value: 'refund', label: 'Refunds' },
  { value: 'voided', label: 'Voided' },
  { value: 'training', label: 'Training' },
];

function toStatusFilter(value: unknown): StatusFilter {
  return STATUS_OPTIONS.some((o) => o.value === value) ? (value as StatusFilter) : 'all';
}

/** The real row shape of `GET /transactions` — narrower than `TransactionSummary`. */
interface TxnRow {
  id: string;
  registerId: number | null;
  cashierId: number | null;
  customerId: number | null;
  cashierName: string | null;
  customerName: string | null;
  customerCompanyName: string | null;
  total: number;
  taxTotal: number;
  subtotal: number;
  itemCount: number;
  status: TransactionStatus;
  legacyTick: string | null;
  legacyGuid: string | null;
  source: string;
  paymentMethod: string;
  occurredAt: string;
}

interface TxnListResponse {
  rows: TxnRow[];
  total: number;
  limit: number;
  offset: number;
  summary?: {
    count: number;
    gross: number;
    completed: number;
    refunds: number;
    voided: number;
    suspended: number;
    itemCount: number;
  };
}

interface TxnSearch {
  id?: string;
  range: RangePreset;
  from?: string;
  to?: string;
  status: StatusFilter;
  register?: number;
  cashier?: number;
  customer?: number;
  q: string;
  page: number;
  /** Structured receipt filters — see features/transactions/queryParse. */
  amountMin?: number;
  amountMax?: number;
  last4?: string;
  authCode?: string;
  tenderType?: string;
  itemQuery?: string;
}

function optionalId(value: unknown): number | undefined {
  const n = Number(value);
  return Number.isInteger(n) && n > 0 ? n : undefined;
}

export const Route = createFileRoute('/_app/transactions')({
  staticData: { crumb: 'Transactions' },
  validateSearch: (s: Record<string, unknown>): TxnSearch => ({
    id: asOptionalText(s.id),
    range: isRangePreset(s.range) ? s.range : 'today',
    from: asOptionalText(s.from),
    to: asOptionalText(s.to),
    status: toStatusFilter(s.status),
    register: optionalId(s.register),
    cashier: optionalId(s.cashier),
    customer: optionalId(s.customer),
    q: asText(s.q, ''),
    page: asPage(s.page),
    amountMin: asInt(s.amountMin),
    amountMax: asInt(s.amountMax),
    last4: asOptionalText(s.last4),
    authCode: asOptionalText(s.authCode),
    tenderType: asOptionalText(s.tenderType),
    itemQuery: asOptionalText(s.itemQuery),
  }),
  component: TransactionsScreen,
});

function TransactionsScreen() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  const [rawQuery, setRawQuery] = useState(search.q);
  const debouncedQuery = useDebouncedValue(rawQuery);

  useShortcutHint([
    { keys: 'j / k', label: 'Move' },
    { keys: 'Enter', label: 'Inspect' },
  ]);

  function patch(next: Partial<TxnSearch>) {
    void navigate({ search: (prev: TxnSearch) => ({ ...prev, ...next }) });
  }

  // The typed box drives the URL through a debounce, replacing rather than
  // pushing so a search does not fill the back stack.
  useEffect(() => {
    if (debouncedQuery !== search.q) {
      void navigate({
        search: (prev: TxnSearch) => ({ ...prev, q: debouncedQuery, page: 1 }),
        replace: true,
      });
    }
  }, [debouncedQuery, search.q, navigate]);

  const registers = useQuery({
    queryKey: ['registers'],
    queryFn: () => api.get<RegisterSummary[]>('/registers'),
    staleTime: 5 * 60_000,
  });

  const users = useQuery({
    queryKey: ['users'],
    queryFn: () => api.get<UserSummary[]>('/users'),
    staleTime: 5 * 60_000,
  });

  const registerName = useMemo(() => {
    const map = new Map<number, string>();
    for (const r of registers.data ?? []) map.set(r.id, r.name);
    return map;
  }, [registers.data]);

  // The URL is the single source of truth for what is being searched; the bar
  // is a view onto it, so a shared link reproduces the search exactly.
  const activeFilters: TxnFilters = useMemo(
    () => ({
      amountMin: search.amountMin,
      amountMax: search.amountMax,
      last4: search.last4,
      authCode: search.authCode,
      tenderType: search.tenderType,
      itemQuery: search.itemQuery,
    }),
    [
      search.amountMin,
      search.amountMax,
      search.last4,
      search.authCode,
      search.tenderType,
      search.itemQuery,
    ]
  );

  const range = resolveRange(search.range, { from: search.from, to: search.to });

  const listParams = {
    date_from: range.from,
    date_to: range.to,
    register_id: search.register,
    cashier_id: search.cashier,
    customer_id: search.customer,
    status: search.status === 'all' ? undefined : search.status,
    search: search.q || undefined,
    amount_min: search.amountMin,
    amount_max: search.amountMax,
    last4: search.last4,
    auth_code: search.authCode,
    tender_type: search.tenderType,
    item_query: search.itemQuery,
    limit: PAGE_SIZE,
    offset: (search.page - 1) * PAGE_SIZE,
  };

  const list = useQuery({
    queryKey: ['transactions', 'list', listParams],
    queryFn: () => api.get<TxnListResponse>('/transactions', { params: listParams }),
    placeholderData: (prev) => prev,
  });

  const rows = list.data?.rows;
  const total = list.data?.total ?? 0;
  const summary = list.data?.summary;

  const columns: Array<Column<TxnRow>> = [
    {
      id: 'occurredAt',
      header: 'When',
      width: '6.5rem',
      mono: true,
      cell: (row) => (
        <span title={dateTime(row.occurredAt)}>{smartDate(row.occurredAt)}</span>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      width: '6rem',
      cell: (row) => <TxnStatusBadge status={row.status} />,
    },
    {
      id: 'register',
      header: 'Register',
      width: '7rem',
      hideBelow: 'lg',
      cell: (row) => (
        <span className="truncate text-xs text-ink-muted">
          {row.registerId === null
            ? '—'
            : (registerName.get(row.registerId) ?? `Register ${row.registerId}`)}
        </span>
      ),
    },
    {
      id: 'cashier',
      header: 'Cashier',
      width: '7.5rem',
      hideBelow: 'xl',
      cell: (row) => (
        <span className="truncate text-xs text-ink-muted">{row.cashierName ?? '—'}</span>
      ),
    },
    {
      id: 'customer',
      header: 'Customer',
      cell: (row) => (
        <span className="flex min-w-0 items-center gap-1.5">
          <span className="truncate text-xs text-ink">
            {row.customerCompanyName || row.customerName || 'Walk-in'}
          </span>
          {row.source === 'legacy' ? <Badge tone="neutral">Legacy</Badge> : null}
        </span>
      ),
    },
    {
      id: 'paymentMethod',
      header: 'Tender',
      width: '6rem',
      hideBelow: 'xl',
      cell: (row) => (
        <span className="truncate text-xs text-ink-muted">
          {row.paymentMethod === 'unknown' ? '—' : humanize(row.paymentMethod)}
        </span>
      ),
    },
    {
      id: 'itemCount',
      header: 'Items',
      width: '3.5rem',
      align: 'right',
      mono: true,
      cell: (row) => num(row.itemCount),
    },
    {
      id: 'total',
      header: 'Total',
      width: '6.5rem',
      align: 'right',
      mono: true,
      cell: (row) => (
        <span className={row.total < 0 ? 'text-negative' : undefined}>
          {money(row.total)}
        </span>
      ),
    },
  ];

  const filtersActive =
    search.status !== 'all' ||
    search.register !== undefined ||
    search.cashier !== undefined ||
    search.customer !== undefined ||
    search.q !== '' ||
    search.range !== 'today';

  return (
    <Page>
      <PageHeader
        eyebrow="Selling"
        title="Transactions"
        subtitle={describeRange(search.range, range)}
      />

      <Toolbar>
        <DateRangeBar
          preset={search.range}
          from={search.from}
          to={search.to}
          onChange={(next) =>
            patch({
              range: next.preset,
              from: next.from,
              to: next.to,
              page: 1,
            })
          }
        />
        <SearchBar
          className="ml-auto w-full max-w-2xl"
          text={rawQuery}
          onTextChange={setRawQuery}
          filters={activeFilters}
          onFiltersChange={(next) =>
            patch({
              amountMin: next.amountMin,
              amountMax: next.amountMax,
              last4: next.last4,
              authCode: next.authCode,
              tenderType: next.tenderType,
              itemQuery: next.itemQuery,
              page: 1,
            })
          }
        />
      </Toolbar>

      <Toolbar>
        <ChipGroup<StatusFilter>
          value={search.status}
          onChange={(v) => patch({ status: v, page: 1 })}
          options={STATUS_OPTIONS}
        />
        <div className="h-4 w-px bg-line" />
        <Select
          size="sm"
          className="w-40"
          aria-label="Register"
          value={search.register === undefined ? '' : String(search.register)}
          onChange={(e) =>
            patch({ register: optionalId(e.currentTarget.value), page: 1 })
          }
        >
          <option value="">Every register</option>
          {(registers.data ?? []).map((r) => (
            <option key={r.id} value={r.id}>
              {r.name}
            </option>
          ))}
        </Select>
        <Select
          size="sm"
          className="w-44"
          aria-label="Cashier"
          value={search.cashier === undefined ? '' : String(search.cashier)}
          onChange={(e) => patch({ cashier: optionalId(e.currentTarget.value), page: 1 })}
        >
          <option value="">Every cashier</option>
          {(users.data ?? []).map((u) => (
            <option key={u.id} value={u.id}>
              {`${u.firstName} ${u.lastName}`.trim() || u.username}
            </option>
          ))}
        </Select>
        {filtersActive ? (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setRawQuery('');
              patch({
                status: 'all',
                register: undefined,
                cashier: undefined,
                customer: undefined,
                q: '',
                range: 'today',
                from: undefined,
                to: undefined,
                page: 1,
              });
            }}
          >
            Reset filters
          </Button>
        ) : null}
      </Toolbar>

      <div className="shrink-0 border-b border-line px-4 py-3">
        <StatRow className="md:grid-cols-3 xl:grid-cols-6">
          <Stat
            label="Gross"
            value={summary ? money(summary.gross) : '—'}
            sub="Every status in range"
            tone={summary && summary.gross < 0 ? 'negative' : undefined}
          />
          <Stat label="Receipts" value={summary ? num(summary.count) : '—'} />
          <Stat label="Completed" value={summary ? num(summary.completed) : '—'} />
          <Stat
            label="Refunds"
            value={summary ? num(summary.refunds) : '—'}
            tone={summary && summary.refunds > 0 ? 'warn' : undefined}
          />
          <Stat
            label="Voided"
            value={summary ? num(summary.voided) : '—'}
            tone={summary && summary.voided > 0 ? 'negative' : undefined}
          />
          <Stat label="Items" value={summary ? num(summary.itemCount) : '—'} />
        </StatRow>
      </div>

      <PageBody bleed className="min-h-0 overflow-hidden">
        <SplitView
          storageKey="transactions"
          onCloseDetail={() => patch({ id: undefined })}
          placeholder={
            <EmptyState
              compact
              icon={<ReceiptIcon />}
              title="No receipt selected"
              description="Pick a row to read the whole receipt — lines, tenders and change — without leaving the list."
            />
          }
          detail={
            search.id ? (
              <TransactionInspector key={search.id} id={search.id} />
            ) : undefined
          }
          list={
            <DataTable
              rows={rows}
              columns={columns}
              rowKey={(row) => row.id}
              loading={list.isLoading}
              error={list.error}
              onRetry={() => void list.refetch()}
              onRowOpen={(row) => patch({ id: row.id })}
              isRowActive={(row) => row.id === search.id}
              rowTone={(row) =>
                row.status === 'voided'
                  ? 'negative'
                  : row.status === 'refund'
                    ? 'warn'
                    : row.status === 'suspended'
                      ? 'info'
                      : null
              }
              empty={{
                icon: <ReceiptIcon />,
                title: 'No receipts in this window',
                description: 'Widen the date range or clear the filters.',
              }}
              footer={
                <Pagination
                  className="w-full"
                  page={search.page}
                  pageSize={PAGE_SIZE}
                  total={total}
                  onPageChange={(page) => patch({ page })}
                />
              }
            />
          }
        />
      </PageBody>
    </Page>
  );
}

/* ── Inspector ─────────────────────────────────────────────────────────────── */

function TransactionInspector({ id }: { id: string }) {
  const valid = isUuid(id);

  const detail = useQuery({
    queryKey: ['transaction', id],
    queryFn: async () =>
      mapTransactionDetail(await api.get<unknown>(`/transactions/${id}`)),
    enabled: valid,
  });

  if (!valid) {
    return (
      <div className="p-3">
        <EmptyState
          compact
          title="That receipt id is not readable"
          description={`“${id}” is not a receipt id. Clear the selection and pick a row from the list.`}
        />
      </div>
    );
  }

  if (detail.isLoading) {
    return (
      <div className="space-y-3 p-3">
        <Skeleton className="h-5 w-40" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (detail.error || !detail.data) {
    return (
      <div className="p-3">
        <ErrorState
          title="Could not load this receipt"
          message={errorMessage(detail.error)}
          onRetry={() => void detail.refetch()}
        />
      </div>
    );
  }

  const txn = detail.data;

  return (
    <div className="space-y-3 p-3">
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <TxnStatusBadge status={txn.status} />
            <Code copyable>{txn.id.slice(0, 8).toUpperCase()}</Code>
          </div>
          <div className="mt-1 font-mono text-xl tabular-nums text-ink">
            {money(txn.total)}
          </div>
          <div className="text-2xs text-ink-faint">{dateTime(txn.occurredAt)}</div>
        </div>
        <Button size="sm" icon={<ExternalLink className="size-3.5" />} asChild>
          <Link to="/transactions/$id" params={{ id: txn.id }}>
            Full receipt
          </Link>
        </Button>
      </div>

      {txn.overReturnFlags.length > 0 ? (
        <div className="rounded border border-warn/35 bg-warn-muted px-2.5 py-2 text-xs text-ink">
          This receipt has been returned against more than it sold — flagged{' '}
          {txn.overReturnFlags.length}{' '}
          {txn.overReturnFlags.length === 1 ? 'time' : 'times'}.
        </div>
      ) : null}

      <Panel title="Lines" bodyClassName="p-3 pt-1">
        <ReceiptLines items={txn.items} />
      </Panel>

      <Panel title="Tenders" bodyClassName="p-3 pt-1">
        <ReceiptTenders tenders={txn.tenders} />
        <div className="mt-2 border-t border-line pt-2">
          <ReceiptTotals
            subtotal={txn.subtotal}
            discountTotal={txn.discountTotal}
            taxTotal={txn.taxTotal}
            total={txn.total}
            tenders={txn.tenders}
          />
        </div>
      </Panel>

      <Panel title="Attribution">
        <DetailList columns={2}>
          <Detail label="Cashier">{txn.cashierName ?? '—'}</Detail>
          <Detail label="Register">{txn.registerName ?? '—'}</Detail>
          <Detail label="Customer">
            {txn.customerCompanyName || txn.customerName || 'Walk-in'}
          </Detail>
          <Detail label="Shift" mono>
            {txn.shiftId ?? '—'}
          </Detail>
        </DetailList>
      </Panel>
    </div>
  );
}
